import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Copy, ExternalLink,
  IdCard, RefreshCw, Search, ShieldCheck, ShieldQuestion, UserRound,
  X, XCircle,
} from 'lucide-react';
import client from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './components/ConfirmDialog';
import { AdminEmpty, AdminError, AdminSkeleton } from './components/PageState';
import { formatDateTime } from './lib/formatters';
import { PAGE_SIZES, PAGE_SIZE_OPTIONS, pageWindow, readPageSize, writePageSize }
  from './lib/pagination';
import './kyc.css';

// Sekmeler backend'in durum kümesiyle birebir aynıdır
// (services/kyc_admin.STATUSES); ayrışırsa uç 400 döner.
export const TABS = [
  { id: 'all', label: 'Tümü', tone: 'neutral' },
  { id: 'pending', label: 'Bekleyen', tone: 'gold' },
  { id: 'approved', label: 'Onaylı', tone: 'green' },
  { id: 'rejected', label: 'Geri Alınan', tone: 'red' },
  // Hiç başvurusu olmayan üyeler. Başvuru tablosunda satırları olmadığı için
  // bu sekme olmadan KYC ekranında hiç görünmüyorlardı — "tüm üyelerin KYC'si
  // listede yok" şikâyetinin kaynağı buydu.
  { id: 'missing', label: 'Kaydı Yok', tone: 'blue' },
];

// Seçenek kümesi panelin bütün liste ekranlarıyla ortaktır; ayrı bir
// liste tutmak aynı panelin bir ekranında 200, diğerinde 500 tavanı
// göstermek olurdu. Dışarı verilmeye devam eder: testler buradan okur.
export { PAGE_SIZES };

const STATUS_LABELS = {
  pending: 'Bekliyor',
  approved: 'Onaylı',
  rejected: 'Geri Alınan',
  missing: 'Kaydı Yok',
};

// Sayfa çubuğu hesabı Spor Bahisleri listesinde de kullanılıyor; ikinci bir
// kopya yazılsaydı aynı çubuk iki ekranda farklı davranırdı. Buradan yeniden
// ihraç edilir, böylece bu modülün dışa açık yüzü değişmez.
export { pageWindow };

const formatBirthDate = (value) => {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) return value;
  return `${match[3]}.${match[2]}.${match[1]}`;
};

const initials = (row) => {
  const source = `${row.first_name || ''}${row.last_name || ''}`.trim()
    || row.username || 'ÜY';
  return source.slice(0, 2).toLocaleUpperCase('tr-TR');
};

const StatusBadge = ({ status }) => (
  <span className={`kyc-status kyc-status-${status}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const Kyc = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const settings = useSettings();
  // Kayıtta otomatik KYC kapalıyken bu ekranın işi onay vermektir; liste
  // bekleyen başvurularla açılır. Açıkken kuyruk pratikte boştur ve ekran
  // bütün kayıtları gözden geçirmeye yarar (bkz. Ayarlar → Site Ayarları).
  const manualReview = settings.kyc_auto_approve === false;

  const [status, setStatus] = useState(manualReview ? 'pending' : 'all');
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => readPageSize('kyc'));

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const searchRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/kyc/requests', {
        params: {
          status,
          q: search || undefined,
          skip: page * pageSize,
          limit: pageSize,
        },
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
      setCounts(data?.counts || {});
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'KYC kayıtları yüklenemedi.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [status, search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Son sayfadaki kayıtlar silinir/karara bağlanırsa istenen sayfa boşalır;
  // yönetici boş bir tabloya bakmak yerine dolu son sayfaya çekilir.
  useEffect(() => {
    if (!loading && page > 0 && page > pageCount - 1) setPage(pageCount - 1);
  }, [loading, page, pageCount]);

  const changeStatus = (next) => {
    setStatus(next);
    setPage(0);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setSearch(term.trim());
    setPage(0);
  };

  const clearSearch = () => {
    setTerm('');
    setSearch('');
    setPage(0);
    searchRef.current?.focus();
  };

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast({ title: `${label} kopyalandı` });
    } catch (copyError) {
      toast({
        title: 'Kopyalanamadı',
        description: 'Tarayıcı panoya yazma iznini vermedi.',
      });
    }
  };

  const askDecision = (row, next) => {
    setNote('');
    setDecision({ row, next });
  };

  const applyDecision = async () => {
    if (!decision) return;
    const { row, next } = decision;
    const reason = note.trim();
    // Ret bir karardır ve üyeye görünür: gerekçesiz bırakılırsa üye neyi
    // düzeltmesi gerektiğini bilemez.
    if (next === 'rejected' && !reason) {
      toast({
        title: 'Gerekçe zorunlu',
        description: 'Reddetme nedenini yazmadan işlem yapılamaz.',
      });
      return;
    }
    setBusy(true);
    try {
      await client.patch(`/admin/kyc/requests/${row.id}`, {
        status: next,
        admin_note: reason,
      });
      toast({
        title: next === 'approved'
          ? 'KYC doğrulaması onaylandı'
          : row.status === 'pending'
            ? 'KYC başvurusu reddedildi'
            : 'KYC doğrulaması geri alındı',
        description: row.username,
      });
      setDecision(null);
      setNote('');
      await load();
    } catch (requestError) {
      toast({
        title: 'İnceleme kaydedilemedi',
        description: requestError.response?.data?.detail || 'Bir hata oluştu.',
      });
    } finally {
      setBusy(false);
    }
  };

  const rangeLabel = useMemo(() => {
    if (!total) return '0 kayıt';
    const first = page * pageSize + 1;
    const last = Math.min(total, (page + 1) * pageSize);
    return `${first}-${last} / ${total}`;
  }, [page, pageSize, total]);

  const activeTab = TABS.find((tab) => tab.id === status) || TABS[0];

  const emptyDescription = search
    ? `“${search}” aramasına uyan kayıt bulunamadı.`
    : status === 'missing'
      ? 'Her üyenin en az bir KYC kaydı var.'
      : 'Bu durumda incelenecek kimlik bilgisi yok.';

  return (
    <div className="admin-page kyc-page">
      <div className="admin-page-heading">
        <div className="admin-page-heading-icon"><ShieldCheck size={22} /></div>
        <div>
          <h1>KYC Onayları</h1>
          <p>
            Üyelerin kimlik bilgileri, verilen kararlar ve hiç başvurusu olmayan
            hesaplar tek listede. Kayıtlar sayfa sayfa gelir; arama kullanıcı
            adı, ad soyad, T.C. kimlik numarası ve üye kimliğinde çalışır.
          </p>
        </div>
        <button
          type="button"
          className="admin-btn-secondary kyc-refresh"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          Yenile
        </button>
      </div>

      <div className={`kyc-mode ${manualReview ? 'manual' : 'auto'}`}>
        <span className="kyc-mode-icon">
          {manualReview ? <ShieldQuestion size={17} /> : <ShieldCheck size={17} />}
        </span>
        <div>
          <b>{manualReview ? 'Onay sizde' : 'Kayıtta otomatik doğrulama açık'}</b>
          <p>
            {manualReview
              ? 'Kayıt kimlik sormaz; üyeler bilgilerini KYC Doğrulama '
                + 'sayfasından gönderir ve Bekleyen sekmesinde onayınızı '
                + 'bekler. Onaylanan hesap para çekebilir.'
              : 'Kimlik bilgileri kayıt sırasında alınır ve anında doğrulanır. '
                + 'Bu ekran kayıtları gözden geçirmeye, gerektiğinde bir '
                + 'hesabın doğrulamasını geri almaya yarar.'}
          </p>
        </div>
        <span className="kyc-mode-path">Ayarlar → Site Ayarları → Kayıtta Otomatik KYC</span>
      </div>

      <div className="kyc-stats">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`stat-card kyc-stat ${tab.tone}${status === tab.id ? ' active' : ''}`}
            onClick={() => changeStatus(tab.id)}
            aria-pressed={status === tab.id}
          >
            <span className="kyc-stat-label">{tab.label}</span>
            <span className="kyc-stat-value">{counts[tab.id] ?? '—'}</span>
          </button>
        ))}
      </div>

      <div className="kyc-toolbar">
        <form className="kyc-search" onSubmit={submitSearch} role="search">
          <div className="kyc-search-field">
            <Search size={16} className="kyc-search-icon" aria-hidden="true" />
            <input
              ref={searchRef}
              className={`admin-input pl-10${term ? ' pr-10' : ''}`}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Kullanıcı adı, ad soyad, T.C. no veya üye kimliği…"
              aria-label="KYC kayıtlarında ara"
            />
            {term && (
              <button
                type="button"
                className="kyc-search-clear"
                onClick={clearSearch}
                aria-label="Aramayı temizle"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button type="submit" className="admin-btn-primary kyc-search-submit">
            Ara
          </button>
        </form>

        <div className="admin-tabs kyc-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={status === tab.id ? 'active' : ''}
              onClick={() => changeStatus(tab.id)}
            >
              {tab.label}
              {counts[tab.id] !== undefined && (
                <span className="kyc-tab-count">{counts[tab.id]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {search && !loading && !error && (
        <div className="kyc-result">
          <b>“{search}”</b> araması — {activeTab.label} sekmesinde {total} kayıt
          <button type="button" className="kyc-result-clear" onClick={clearSearch}>
            aramayı temizle
          </button>
        </div>
      )}

      {error && <AdminError message={error} onRetry={load} />}
      {loading && <AdminSkeleton rows={6} label="KYC kayıtları yükleniyor" />}

      {!loading && !error && (
        <div className="admin-card kyc-panel">
          <div className="kyc-panel-head">
            <IdCard size={16} aria-hidden="true" />
            <h2>{activeTab.label}</h2>
            <span className="kyc-panel-count">{rangeLabel}</span>
          </div>

          {items.length === 0 ? (
            <AdminEmpty title="Kayıt bulunamadı" description={emptyDescription} />
          ) : (
            <div className="admin-table-wrap kyc-table-wrap">
              <table className="admin-table kyc-table">
                <thead>
                  <tr>
                    <th>Üye</th>
                    <th>Ad Soyad</th>
                    <th>T.C. Kimlik No</th>
                    <th>Doğum Tarihi</th>
                    <th>Gönderim</th>
                    <th>Durum</th>
                    <th>Karar</th>
                    <th className="text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id || `user-${row.user_id}`}>
                      <td data-label="Üye">
                        <div className="kyc-user">
                          <span className="kyc-avatar" aria-hidden="true">
                            {initials(row)}
                          </span>
                          <div className="kyc-user-text">
                            <span className="kyc-username">
                              {row.username || row.user_id}
                              {row.account_banned && (
                                <span className="badge-pill badge-red">Yasaklı</span>
                              )}
                              {!row.account_exists && (
                                <span className="badge-pill badge-gray">Hesap silinmiş</span>
                              )}
                            </span>
                            <button
                              type="button"
                              className="kyc-copy"
                              onClick={() => copyValue(row.user_id, 'Üye kimliği')}
                              title="Üye kimliğini kopyala"
                            >
                              {row.user_id}
                              <Copy size={11} aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td data-label="Ad Soyad" className="kyc-name">
                        {`${row.first_name || ''} ${row.last_name || ''}`.trim() || '—'}
                      </td>
                      <td data-label="T.C. Kimlik No">
                        {row.tc_no ? (
                          <button
                            type="button"
                            className="kyc-copy kyc-tc"
                            onClick={() => copyValue(row.tc_no, 'T.C. kimlik numarası')}
                            title="T.C. kimlik numarasını kopyala"
                          >
                            {row.tc_no}
                            <Copy size={11} aria-hidden="true" />
                          </button>
                        ) : <span className="kyc-dim">—</span>}
                      </td>
                      <td data-label="Doğum Tarihi">{formatBirthDate(row.birth_date)}</td>
                      <td data-label="Gönderim" className="kyc-dim">
                        {formatDateTime(row.created_at)}
                      </td>
                      <td data-label="Durum">
                        <div className="kyc-status-cell">
                          <StatusBadge status={row.status} />
                          {/* Karar satırın kendisine aittir; hesabın **şu anki**
                              doğrulaması ondan ayrı olabilir (geri alınmış bir
                              başvurunun sahibi yeniden doğrulanmış olabilir). */}
                          <span className={`kyc-account ${row.account_verified ? 'ok' : 'off'}`}>
                            {row.account_verified ? 'Hesap doğrulanmış' : 'Hesap doğrulanmamış'}
                          </span>
                        </div>
                      </td>
                      <td data-label="Karar">
                        {row.status === 'missing' ? (
                          <span className="kyc-dim">Başvuru yok</span>
                        ) : (
                          <div className="kyc-review">
                            <span>
                              {row.reviewed_by_username
                                ? row.reviewed_by_username
                                : <span className="kyc-dim">Otomatik</span>}
                            </span>
                            <span className="kyc-dim">
                              {formatDateTime(row.reviewed_at, '—')}
                            </span>
                            {row.admin_note && (
                              <span className="kyc-note" title={row.admin_note}>
                                {row.admin_note}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td data-label="İşlem">
                        <div className="kyc-actions">
                          {row.id && row.status !== 'approved' && (
                            <button
                              type="button"
                              className="kyc-action approve"
                              onClick={() => askDecision(row, 'approved')}
                            >
                              <CheckCircle2 size={14} /> Onayla
                            </button>
                          )}
                          {row.id && row.status !== 'rejected' && (
                            <button
                              type="button"
                              className="kyc-action reject"
                              onClick={() => askDecision(row, 'rejected')}
                            >
                              <XCircle size={14} />
                              {row.status === 'pending' ? 'Reddet' : 'Geri al'}
                            </button>
                          )}
                          <button
                            type="button"
                            className="kyc-action open"
                            onClick={() => navigate(`/admin/users/${row.user_id}`)}
                            title="Üye profilini aç"
                          >
                            <UserRound size={14} /> Üye
                            <ExternalLink size={11} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="kyc-pager">
            <label className="kyc-page-size">
              Sayfa başına
              <select
                className="admin-input"
                value={pageSize}
                onChange={(event) => {
                  const size = Number(event.target.value);
                  setPageSize(size);
                  writePageSize('kyc', size);
                  setPage(0);
                }}
                aria-label="Sayfa başına kayıt"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="kyc-pager-nav">
              <button
                type="button"
                className="kyc-page-btn"
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                disabled={page === 0}
                aria-label="Önceki sayfa"
              >
                <ChevronLeft size={15} />
              </button>
              {pageWindow(page, pageCount).map((value, index) => (
                value === 'gap' ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={`gap-${index}`} className="kyc-page-gap">…</span>
                ) : (
                  <button
                    key={value}
                    type="button"
                    className={`kyc-page-btn${value === page ? ' active' : ''}`}
                    onClick={() => setPage(value)}
                    aria-current={value === page ? 'page' : undefined}
                  >
                    {value + 1}
                  </button>
                )
              ))}
              <button
                type="button"
                className="kyc-page-btn"
                onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
                disabled={page >= pageCount - 1}
                aria-label="Sonraki sayfa"
              >
                <ChevronRight size={15} />
              </button>
            </div>

            <span className="kyc-pager-range">{rangeLabel}</span>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(decision)}
        title={decision?.next === 'approved'
          ? 'Doğrulamayı onayla'
          : decision?.row?.status === 'pending'
            ? 'Başvuruyu reddet'
            : 'Doğrulamayı geri al'}
        description={decision ? (decision.next === 'approved'
          ? `${decision.row.username} hesabı doğrulanacak, kimlik bilgileri `
            + 'üye kaydına yazılacak ve bilgilendirme e-postası gönderilecek.'
          : `${decision.row.username} hesabının doğrulaması kaldırılacak; `
            + 'gerekçe üyenin KYC sayfasında görünür.') : ''}
        confirmLabel={decision?.next === 'approved' ? 'Onayla' : 'Uygula'}
        tone={decision?.next === 'approved' ? 'warning' : 'danger'}
        busy={busy}
        extra={(
          <label className="kyc-note-field">
            {decision?.next === 'approved' ? 'Not (isteğe bağlı)' : 'Gerekçe (zorunlu)'}
            <textarea
              className="admin-input"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={decision?.next === 'approved'
                ? 'Belgeler kontrol edildi…'
                : 'Kimlik bilgileri okunamıyor…'}
            />
          </label>
        )}
        onCancel={() => { if (!busy) { setDecision(null); setNote(''); } }}
        onConfirm={applyDecision}
      />

      {status === 'missing' && !loading && !error && items.length > 0 && (
        <p className="kyc-hint">
          <AlertTriangle size={14} aria-hidden="true" />
          Bu üyelerin hiç KYC başvurusu yok: kimliklerini hiç göndermediler ya da
          eski bir kayıtta doğrulamaları kimlik bilgisi olmadan elle işaretlendi.
          Güncel <b>Kullanıcılar → Düzenle</b> penceresi T.C. kimlik no ve doğum
          tarihiyle onay verdiğinde otomatik KYC kaydı oluşturur. Karara bağlanacak
          bir başvuru olmadığı için bu satırlarda onay düğmesi çizilmez.
        </p>
      )}
    </div>
  );
};

export default Kyc;
